import React, { forwardRef } from 'react';
import dayjs from 'dayjs';
import paper from '../../../components/documents-templates/report/ReportPaper.module.css';
import Header from '../../../components/documents-templates/header/Header';
import Footer from '../../../components/documents-templates/footer/Footer';
import { companyHeaderProps } from '../../../components/documents-templates/report/reportCompany';
import stats from '../components/stats.module.css';
import { StatsPrintContext } from '../components/printContext';
import { StatsCompareContext } from '../components/compareContext';
import { ROLE_LOCKED_TEXT } from '../components/SectionState';
import STATS_SECTION_LIST from '../sections';
import { monthlyGoal } from '../utils/goal';
import { compareLabels, describePeriod } from '../utils/periods';
import styles from './StatisticsReport.module.css';

/**
 * PDF das estatísticas: cabeçalho da clínica, período e as mesmas seções da
 * tela (gráficos sem animação, nomes sem link). Seções bloqueadas pelo plano
 * ou que falharam ao carregar aparecem só com uma linha de aviso.
 *
 * `sections`: estado do useClinicStats no momento da exportação.
 * `ownAgenda`: visão da Equipe (só a própria agenda, sem valores); as áreas da
 * conta inteira saem só com a linha de aviso, como na tela.
 */
const unavailableText = (state) => {
  if (state && state.status === 'locked') return 'Disponível a partir do plano Profissional.';
  if (state && state.status === 'roleLocked') return ROLE_LOCKED_TEXT;
  return 'Não foi possível carregar esta área.';
};

const StatisticsReport = forwardRef(({ companyData, period, sections, ownAgenda = false }, ref) => {
  const company = companyData || {};
  const compare = compareLabels(period);

  return (
    <div ref={ref} className={paper.paper}>
      {company.Name && <Header {...companyHeaderProps(company)} />}

      <div className={paper.titleBlock}>
        <h2 className={paper.title}>{ownAgenda ? 'Estatísticas da sua agenda' : 'Estatísticas da clínica'}</h2>
        <span className={paper.subtitle}>
          {describePeriod(period.start, period.end)}
          {ownAgenda ? ' · só a sua agenda, sem valores' : ''}
          {`${compare.enabled ? ` · ${compare.long}` : ''} · gerado em `}
          {dayjs().format('DD/MM/YYYY [às] HH:mm')}
        </span>
      </div>

      <StatsPrintContext.Provider value>
        <StatsCompareContext.Provider value={compare}>
          <div className={stats.print}>
            {STATS_SECTION_LIST.map(({ id, label, description, Component }) => {
              const state = sections[id];
              const ready = state && state.status === 'ready';
              return (
                <div key={id} className={styles.section}>
                  <div className={styles.sectionHead}>
                    <h3>{label}</h3>
                    <span>{description}</span>
                  </div>
                  {ready
                    ? <Component data={state.data} granularity={state.granularity} period={period} goal={monthlyGoal(company)} />
                    : <span className={styles.unavailable}>{unavailableText(state)}</span>}
                </div>
              );
            })}
          </div>
        </StatsCompareContext.Provider>
      </StatsPrintContext.Provider>

      <Footer />
    </div>
  );
});

StatisticsReport.displayName = 'StatisticsReport';

export default StatisticsReport;
